/**
 * Dashboard helpers shared by the builder, the viewer, and the data layer.
 *
 * Nothing here performs I/O — these are pure functions so widget maths can be
 * reasoned about (and tested) without a live GIS service.
 */

import type {
  DashboardField,
  DashboardWidget,
  FilterClause,
  StatisticType,
  WidgetLayout,
  WidgetType,
} from '../types/dashboardTypes';
import { VALUELESS_OPERATORS } from '../types/dashboardTypes';

/** Columns in the layout grid. Widget `w` is expressed in these units. */
export const GRID_COLUMNS = 12;
/** Pixel height of one grid row; widget `h` multiplies this. */
/** Fallback row height, used only before the container has been measured. */
export const GRID_ROW_HEIGHT = 72;
/**
 * One spacing value for everything — the gap BETWEEN tiles and the padding
 * around the grid. Using a different page padding is what made the outer
 * margin look unlike the inner gutters.
 */
export const GRID_GAP = 6;
/** Tiles never shrink below this, however many rows there are. */
export const GRID_MIN_ROW_HEIGHT = 44;

export const DEFAULT_PALETTE = [
  '#2563EB',
  '#0D9488',
  '#F59E0B',
  '#DC2626',
  '#7C3AED',
  '#0EA5E9',
  '#65A30D',
  '#DB2777',
  '#EA580C',
  '#475569',
];

export function paletteColor(palette: string[] | undefined, index: number): string {
  const colors = palette?.length ? palette : DEFAULT_PALETTE;
  return colors[index % colors.length];
}

/* ─────────────────────────── Filter expressions ─────────────────────────── */

/** Escapes a value for a single-quoted SQL/CQL literal. */
function quote(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * True when the value should be written unquoted.
 *
 * Numeric fields must not be quoted — ArcGIS rejects `diameter > '100'` on an
 * integer column, and GeoServer's CQL treats it as a string comparison.
 */
function isNumericField(field: string, fields: DashboardField[]): boolean {
  return fields.find((entry) => entry.name === field)?.type === 'number';
}

function literal(clause: FilterClause, fields: DashboardField[]): string {
  const raw = clause.value.trim();
  if (isNumericField(clause.field, fields) && raw !== '' && Number.isFinite(Number(raw))) {
    return raw;
  }
  return quote(raw);
}

/**
 * Builds a WHERE expression from filter clauses.
 *
 * The output is standard SQL-92-ish, which both ArcGIS `where` and GeoServer
 * `CQL_FILTER` accept for these operators — hence one builder for both
 * adapters rather than two dialects to keep in sync.
 *
 * Returns '' when no clause is complete, meaning "no filter".
 */
export function buildWhereClause(clauses: FilterClause[], fields: DashboardField[]): string {
  const usable = clauses.filter(
    (clause) =>
      clause.field &&
      (VALUELESS_OPERATORS.includes(clause.operator) || clause.value.trim() !== ''),
  );
  if (usable.length === 0) return '';

  return usable
    .map((clause, index) => {
      const field = clause.field;
      let condition: string;

      switch (clause.operator) {
        case 'isNull':
          condition = `${field} IS NULL`;
          break;
        case 'isNotNull':
          condition = `${field} IS NOT NULL`;
          break;
        case 'contains':
          condition = `${field} LIKE ${quote(`%${clause.value.trim()}%`)}`;
          break;
        case 'startsWith':
          condition = `${field} LIKE ${quote(`${clause.value.trim()}%`)}`;
          break;
        default:
          condition = `${field} ${clause.operator} ${literal(clause, fields)}`;
      }

      return index === 0 ? condition : `${clause.conjunction} ${condition}`;
    })
    .join(' ');
}

/* ──────────────────────── Client-side aggregation ───────────────────────── */

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Applies a statistic to raw values.
 *
 * Used as the fallback path when a service cannot compute statistics
 * server-side (GeoServer WFS has no equivalent of ArcGIS `outStatistics`).
 */
export function aggregate(values: unknown[], statistic: StatisticType): number | null {
  if (statistic === 'count') return values.length;

  const numbers = values.map(toNumber).filter((value): value is number => value !== null);
  if (numbers.length === 0) return null;

  switch (statistic) {
    case 'sum':
      return numbers.reduce((total, value) => total + value, 0);
    case 'avg':
      return numbers.reduce((total, value) => total + value, 0) / numbers.length;
    case 'min':
      return Math.min(...numbers);
    case 'max':
      return Math.max(...numbers);
    default:
      return null;
  }
}

/** Groups records by a category field and aggregates each group. */
export function groupAndAggregate(
  records: Record<string, unknown>[],
  categoryField: string,
  statistic: StatisticType,
  valueField?: string,
): { category: string; value: number }[] {
  const groups = new Map<string, unknown[]>();

  for (const record of records) {
    const raw = record[categoryField];
    // Blank and null collapse into one bucket rather than vanishing, so the
    // chart total still matches the layer's record count.
    const key = raw === null || raw === undefined || String(raw).trim() === ''
      ? '(blank)'
      : String(raw).trim();
    const bucket = groups.get(key) ?? [];
    bucket.push(statistic === 'count' ? raw : record[valueField ?? categoryField]);
    groups.set(key, bucket);
  }

  return Array.from(groups.entries())
    .map(([category, values]) => ({ category, value: aggregate(values, statistic) ?? 0 }))
    .filter((entry) => Number.isFinite(entry.value));
}

export function sortCategories(
  data: { category: string; value: number }[],
  sortBy: string | undefined,
): { category: string; value: number }[] {
  const sorted = [...data];
  switch (sortBy) {
    case 'value-asc':
      return sorted.sort((a, b) => a.value - b.value);
    case 'category-asc':
      return sorted.sort((a, b) => a.category.localeCompare(b.category));
    case 'category-desc':
      return sorted.sort((a, b) => b.category.localeCompare(a.category));
    case 'value-desc':
    default:
      return sorted.sort((a, b) => b.value - a.value);
  }
}

/* ───────────────────────────── Formatting ───────────────────────────────── */

export function formatNumber(value: number | null, decimals = 0): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Compacts large values so a KPI card never overflows its tile. */
export function formatCompact(value: number | null, decimals = 0): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 100_000) return `${(value / 1_000).toFixed(0)}K`;
  return formatNumber(value, decimals);
}

export function formatCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/* ───────────────────────────── Layout maths ─────────────────────────────── */

/** Clamps a widget inside the grid so it can never be dragged out of view. */
export function clampLayout(layout: WidgetLayout): WidgetLayout {
  const w = Math.max(1, Math.min(GRID_COLUMNS, Math.round(layout.w)));
  const h = Math.max(1, Math.round(layout.h));
  return {
    w,
    h,
    x: Math.max(0, Math.min(GRID_COLUMNS - w, Math.round(layout.x))),
    y: Math.max(0, Math.round(layout.y)),
  };
}

/** True when two widget boxes share any grid cell. */
export function collides(a: WidgetLayout, b: WidgetLayout): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  );
}

/**
 * Pushes widgets apart so none overlap, keeping `anchorId` exactly where the
 * admin dropped it.
 *
 * Without this a widget dragged onto an occupied cell would render on top of
 * its neighbour — CSS Grid stacks explicitly-positioned items rather than
 * reflowing them. Displaced widgets move DOWN only, so horizontal intent is
 * preserved and the result is predictable.
 */
export function resolveCollisions(
  widgets: DashboardWidget[],
  anchorId: string,
): DashboardWidget[] {
  const anchor = widgets.find((widget) => widget.id === anchorId);
  if (!anchor) return widgets;

  const placed: WidgetLayout[] = [anchor.layout];
  const resolved = new Map<string, WidgetLayout>([[anchor.id, anchor.layout]]);

  const others = widgets
    .filter((widget) => widget.id !== anchorId)
    .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);

  for (const widget of others) {
    const layout = { ...widget.layout };
    // Bounded by the number of widgets: each pass can only be blocked by rows
    // already occupied, so this always terminates.
    let guard = 0;
    while (placed.some((other) => collides(layout, other)) && guard < 500) {
      layout.y += 1;
      guard += 1;
    }
    placed.push(layout);
    resolved.set(widget.id, layout);
  }

  return widgets.map((widget) => ({ ...widget, layout: resolved.get(widget.id) ?? widget.layout }));
}

/** First free row below everything placed so far — where a new widget lands. */
export function nextFreeRow(widgets: DashboardWidget[]): number {
  return widgets.reduce((lowest, widget) => Math.max(lowest, widget.layout.y + widget.layout.h), 0);
}

/**
 * Reflows widgets into full rows, left to right.
 *
 * Two things make the result read as a grid rather than a pile of cards:
 *  - widgets WRAP when a row runs out of columns, instead of each one dropping
 *    onto its own row the way `nextFreeRow` placement does;
 *  - every widget in a row is given the SAME height, so tops and bottoms line
 *    up across the row. Ragged tile heights are what made the old layout look
 *    unaligned even though the gaps were already uniform.
 *
 * `stretch` additionally scales a row's widths up to fill all 12 columns, which
 * removes the dead space on the right. It is deliberately opt-in: tidying
 * positions when a widget is added should not silently resize the widgets the
 * admin already sized by hand.
 */
export function packWidgets(
  widgets: DashboardWidget[],
  options: { stretch?: boolean } = {},
): DashboardWidget[] {
  if (widgets.length === 0) return widgets;

  const ordered = [...widgets].sort(
    (a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x,
  );

  // Group into rows by accumulating widths until the next widget would not fit.
  const rows: DashboardWidget[][] = [];
  let current: DashboardWidget[] = [];
  let used = 0;

  for (const widget of ordered) {
    const width = Math.max(1, Math.min(GRID_COLUMNS, Math.round(widget.layout.w)));
    if (current.length > 0 && used + width > GRID_COLUMNS) {
      rows.push(current);
      current = [];
      used = 0;
    }
    current.push(widget);
    used += width;
  }
  if (current.length > 0) rows.push(current);

  const placed = new Map<string, WidgetLayout>();
  let y = 0;

  for (const row of rows) {
    const widths = row.map((widget) =>
      Math.max(1, Math.min(GRID_COLUMNS, Math.round(widget.layout.w))),
    );
    const height = Math.max(...row.map((widget) => Math.max(1, Math.round(widget.layout.h))));

    let finalWidths = widths;
    if (options.stretch) {
      const total = widths.reduce((sum, width) => sum + width, 0);
      // Scale proportionally, then hand every leftover column to the last tile
      // so the row lands on exactly 12 — rounding alone would leave a sliver.
      const scaled = widths.map((width) => Math.max(1, Math.floor((width * GRID_COLUMNS) / total)));
      const remainder = GRID_COLUMNS - scaled.reduce((sum, width) => sum + width, 0);
      scaled[scaled.length - 1] += remainder;
      finalWidths = scaled;
    }

    // `y` advances per row, so it is captured here rather than closed over —
    // a callback reading the mutable loop variable would see the wrong row.
    const rowY = y;
    let x = 0;
    row.forEach((widget, index) => {
      placed.set(widget.id, { x, y: rowY, w: finalWidths[index], h: height });
      x += finalWidths[index];
    });
    y += height;
  }

  return widgets.map((widget) => ({ ...widget, layout: placed.get(widget.id) ?? widget.layout }));
}

/**
 * Row height that makes `rows` rows fill `availableHeight` exactly.
 *
 * This is what turns the dashboard into a single page: the old fixed 72px row
 * meant total height grew with content until the page had to scroll. Below
 * `GRID_MIN_ROW_HEIGHT` the tiles stop shrinking — a 20px chart is not a
 * dashboard — and the caller is expected to warn rather than quietly scroll.
 */
export function fitRowHeight(availableHeight: number, rows: number): number {
  if (rows <= 0 || availableHeight <= 0) return GRID_ROW_HEIGHT;
  const usable = availableHeight - GRID_GAP * (rows - 1);
  return Math.max(GRID_MIN_ROW_HEIGHT, usable / rows);
}

/** True when the rows genuinely fit, rather than being clamped at the minimum. */
export function rowsFitInHeight(availableHeight: number, rows: number): boolean {
  if (rows <= 0 || availableHeight <= 0) return true;
  return rows * GRID_MIN_ROW_HEIGHT + GRID_GAP * (rows - 1) <= availableHeight;
}

/** Total rows the grid must reserve, including a little room to drop into. */
export function gridRowCount(widgets: DashboardWidget[]): number {
  return Math.max(6, nextFreeRow(widgets) + 2);
}

/* ─────────────────────────── Widget validation ──────────────────────────── */

/**
 * Human-readable reasons a widget cannot render yet.
 *
 * Used by the builder to flag incomplete widgets and by Save & Launch to block
 * publishing a dashboard that would show errors to end users.
 */
export function validateWidget(widget: DashboardWidget): string[] {
  const problems: string[] = [];
  const needsSource: WidgetType[] = [
    'kpi', 'gauge', 'pie', 'donut', 'bar', 'column', 'line', 'table', 'list',
  ];

  if (needsSource.includes(widget.type) && !widget.dataSourceId) {
    problems.push('No data source selected');
  }

  if ((widget.type === 'kpi' || widget.type === 'gauge') && widget.dataSourceId) {
    const statistic = widget.config.statistic ?? 'count';
    if (statistic !== 'count' && !widget.config.field) {
      problems.push(`“${statistic}” needs a numeric field`);
    }
  }

  if (['pie', 'donut', 'bar', 'column', 'line'].includes(widget.type) && widget.dataSourceId) {
    if (!widget.config.categoryField) problems.push('No category field selected');
    const statistic = widget.config.statistic ?? 'count';
    if (statistic !== 'count' && !widget.config.field) {
      problems.push(`“${statistic}” needs a numeric field`);
    }
  }

  if (widget.type === 'list' && widget.dataSourceId && !widget.config.titleField) {
    problems.push(
      widget.config.listMode === 'grouped' ? 'No group-by field selected' : 'No title field selected',
    );
  }

  // A grouped list computing anything other than a count needs a column to
  // compute it on, exactly like a chart.
  if (
    widget.type === 'list' &&
    widget.config.listMode === 'grouped' &&
    widget.dataSourceId &&
    (widget.config.statistic ?? 'count') !== 'count' &&
    !widget.config.field
  ) {
    problems.push('No field selected for the statistic');
  }

  if (widget.type === 'table' && widget.dataSourceId && !(widget.config.columns?.length)) {
    problems.push('No columns selected');
  }

  if (widget.type === 'map' && !(widget.config.mapDataSourceIds?.length)) {
    problems.push('No layers selected');
  }

  if (widget.type === 'gauge') {
    const min = widget.config.minValue ?? 0;
    const max = widget.config.maxValue ?? 100;
    if (max <= min) problems.push('Maximum must be greater than minimum');
  }

  return problems;
}
